import LeafPinLevelName from "../../../../../common/leaf/LeafPinLevelName/LeafPinLevelName";
import { useAppSelector } from "../../../../../store";
import type { LegsProgressItem } from "../../PerformanceTile/LegsProgress/LegsProgress";
import { LegsProgress } from "../../PerformanceTile/LegsProgress/LegsProgress";
import { PerformanceTile } from "../../PerformanceTile/PerformanceTile";
import { PerformanceTileHeader } from "../../PerformanceTile/PerformanceTileHeader/PerformanceTileHeader";
import type { HatGarTrackingResponse } from "../../../../../services/AnnualIncomeDashboard/annualIncomeDashboardAPI.types";
import defaultContent from "../../../../../content/performanceIncomeDashboard";
import { useTranslation } from "react-i18next";
import LeafRectangleLoader from "../../../../../common/leaf/LeafLoadingSkeleton/LeafRectangleLoader/LeafRectangleLoader";
import { FoundersPlatinumProgress } from "../FoundersPlatinumProgress/FoundersPlatinumProgress";
import { useState } from "react";
import { mapHatModalToFppResponse } from "../HatGarTile/hatFppModalMapper";
import { getPerformanceYearShort, getSelectedPerformanceYearData } from "../../../../../common/util/period";
import { LeafNavigationTile } from "../../../../../common/leaf/LeafNavigationTile/LeafNavigationTile";
import TEALIUM from "../../../../../common/enums/tealium-enums";
import { track } from "../../../../../common/util/tealium";
import { QUALIFICATION_STATUS } from "../../../../../common/enums/incentive-enums";
import { parseStatus } from "../utils";
import { ActionCardError } from "../../../GroupManagementDashboard/ActionCard/ActionCardError/ActionCardError";
import { trackFoundersPlatinumStatus } from "../../../../../common/tealium/PerformanceIncomeDashboard/PerformanceIncomeDashboardTrack";
import "./InMarketPinTrackingTile.scss";

interface InMarketPinTrackingTileProps {
  hatGarData: HatGarTrackingResponse;
  isLoading: boolean;
  errorStatus: boolean;
  refetch: () => void;
}

const inMarketPinTileLoader = () => {
  return (
    <div className="InMarketPinTrackingTile" data-testid="in-market-pin-tracking-tile">
      <PerformanceTile>
        <PerformanceTileHeader label={<LeafRectangleLoader isLoading width="8rem" height="1.5rem" margin="0" />} />
        <LegsProgress
          items={[
            {
              label: <LeafRectangleLoader isLoading width="14rem" height="1.25rem" margin="0" />,
              value: <LeafRectangleLoader isLoading width="6rem" height="1.25rem" margin="0" />,
            },
            {
              label: <LeafRectangleLoader isLoading width="12rem" height="1.25rem" margin="0" />,
              value: <LeafRectangleLoader isLoading width="6rem" height="1.25rem" margin="0" />,
            },
          ]}
        />
        <LeafRectangleLoader isLoading width="100%" height="46px" margin="0" />
      </PerformanceTile>
    </div>
  );
};

export const InMarketPinTrackingTile = ({
  hatGarData,
  isLoading,
  errorStatus,
  refetch,
}: InMarketPinTrackingTileProps) => {
  const { selectedPeriod } = useAppSelector((state) => state.period);
  const { t } = useTranslation(["performanceIncomeDashboard"]);
  const [showFppModal, setShowFppModal] = useState(false);

  const showData = useAppSelector((state) => state.showData.showData);

  // Guard: Only show for Persona 2+ (Business Builder and above)
  if (!isLoading && !errorStatus && !hatGarData?.showInMarketPin) {
    return null;
  }

  const hatData = hatGarData?.hatTracking;
  const { selectedPerformanceYear: previousPerformanceYear } = getSelectedPerformanceYearData(selectedPeriod, 1);

  const fppModalData = hatData?.foundersPlatinumStatus?.modal
    ? mapHatModalToFppResponse(hatData.foundersPlatinumStatus.modal)
    : undefined;

  const fppOnCloseClick = () => {
    setShowFppModal(false);
    trackFoundersPlatinumStatus(false);
    track(
      TEALIUM.U_TAG_METHOD.LINK,
      TEALIUM.EVENT_NAME.CLICK_ACTION,
      "FPP_modal",
      TEALIUM.CATEGORY.MY_PERFORMANCE_DASH,
      "",
      "cancel_icon",
    );
  };

  const fppOnOpenClick = () => {
    setShowFppModal(true);
    trackFoundersPlatinumStatus(true);
  };

  const pinItems: LegsProgressItem[] = [
    hatData?.inMarketPinTrackingLevel && {
      testid: "pin-tracking-in-market-level",
      label: t(
        "performance.hatgar.pinTrackingQualifiedLegsAward",
        defaultContent.performance.hatgar.pinTrackingQualifiedLegsAward as string,
      ),
      value: showData ? <LeafPinLevelName pinLevelCode={hatData.inMarketPinTrackingLevel.rank} /> : "--",
    },
    hatData?.inMarketPinLevelPY && {
      testid: "pin-tracking-current-pin",
      label: t(
        "performance.hatgar.currentInMarketPin",
        defaultContent.performance.hatgar.currentInMarketPin as string,
        {
          shortPY: getPerformanceYearShort(previousPerformanceYear),
        },
      ),
      value: showData ? <LeafPinLevelName pinLevelCode={hatData.inMarketPinLevelPY.rank} /> : "--",
    },
    hatData?.highestAwardLevel && {
      testid: "pin-tracking-highest-award",
      label: t("performance.hatgar.highestAwardLevel", defaultContent.performance.hatgar.highestAwardLevel as string),
      value: showData ? <LeafPinLevelName pinLevelCode={hatData.highestAwardLevel.rank} /> : "--",
    },
  ].filter(Boolean) as LegsProgressItem[];

  if (errorStatus) {
    return (
      <div className="InMarketPinTrackingTile">
        <PerformanceTile>
          <PerformanceTileHeader
            label={t("performance.inmarketpin.tileTitle", defaultContent.performance.inmarketpin.tileTitle as string)}
          />
          <ActionCardError onRetry={refetch} />
        </PerformanceTile>
      </div>
    );
  }

  return isLoading ? (
    inMarketPinTileLoader()
  ) : (
    <>
      <div className="InMarketPinTrackingTile" data-testid="in-market-pin-tracking-tile">
        <PerformanceTile>
          <PerformanceTileHeader
            label={
              <span data-testid="pin-tracking-tile-heading">
                {t("performance.inmarketpin.tileTitle", defaultContent.performance.inmarketpin.tileTitle as string)}
              </span>
            }
          />
          <LegsProgress items={pinItems} />
          {hatData?.foundersPlatinumStatus?.show && (
            <div data-testid="founders-platinum-status-link">
              <LeafNavigationTile
                title={t(
                  "performance.hatgar.pinTrackingFpStatus",
                  defaultContent.performance.hatgar.pinTrackingFpStatus as string,
                )}
                iconType="platinum"
                to=""
                handleModalClick={fppOnOpenClick}
              />
            </div>
          )}
        </PerformanceTile>
      </div>
      {fppModalData && (
        <FoundersPlatinumProgress
          show={showFppModal}
          onCloseClick={fppOnCloseClick}
          status={parseStatus(hatData?.foundersPlatinumStatus?.modal?.status || QUALIFICATION_STATUS.NOT_TRACKING)}
          data={fppModalData}
        />
      )}
    </>
  );
};
